import { FC, useEffect, useState } from "react";

import {
  FormType,
  mapNewPermissionsToOldPermissions,
  UserFormPermissions,
  UserGeneralPermissions,
} from "@simplyk/common";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "../../../../constants/amplitude";
import { useCurrentUserContext } from "../../../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../contexts/LocaleContext";
import { trpc } from "../../../../helpers/trpc";
import { useAmplitude } from "../../../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../../../hooks/useTranslate";

import { CampaignPermissionsSection } from "./components/CampaignPermissionsSection";
import { CampaignScopeAutocomplete } from "./components/CampaignScopeAutocomplete";
import { GeneralPermissionsList } from "./components/GeneralPermissionsList";
import { NotificationsSection } from "./components/NotificationsSection";
import { UserInputsForm } from "./components/UserInputsForm";
import { UserPermissionMFAStep } from "./components/UserPermissionMFAStep";
import { buildPermissionMaps } from "./helpers/permissions";
import { useUserPermissionState } from "./hooks/useUserPermissionState";
import { UserPermissionModalLayout } from "./layouts/UserPermissionModalLayout";

import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { AddUser as UserAddIcon } from "@/icons/outlined";

interface InviteUserPermissionModalProps {
  title?: string;
  isOpen: boolean;
  onClose: () => void;
  refetchTable?: () => Promise<void>;
  source?: "default" | "share-modal" | "bank-stepper" | "form-editor";
  formLink?: string;
  defaultGeneralPermissions?: UserGeneralPermissions[];
  defaultFormPermissions?: UserFormPermissions[];
  formId?: string;
  formType?: FormType;
  defaultEmail?: string;
}

const InviteUserPermissionModal: FC<InviteUserPermissionModalProps> = ({
  title,
  isOpen,
  onClose,
  refetchTable,
  source,
  formLink,
  defaultGeneralPermissions: defaultGeneralPermissionsParams,
  defaultFormPermissions,
  formId,
  formType,
  defaultEmail,
}) => {
  const defaultGeneralPermissions = formId ? [] : defaultGeneralPermissionsParams;

  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { currentUser } = useCurrentUserContext();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { generalPermissionMap: currentUserGeneralPermissionMap, formPermissionMap: currentUserFormPermissionMap } =
    buildPermissionMaps(currentUser);

  // Server state layer
  const { mutateAsync: execAddMemberToOrganization, isLoading: isLoadingAddMemberToOrganization } =
    trpc.addMemberToOrganization.useMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Build default permissions
  const defaultFormPermissionsData = defaultFormPermissions
    ? defaultFormPermissions?.reduce(
        (acc, permission) => {
          acc[permission] = currentUserFormPermissionMap[permission] || false;
          return acc;
        },
        {} as Record<UserFormPermissions, boolean>
      )
    : {
        [UserFormPermissions.EditForm]: currentUserFormPermissionMap[UserFormPermissions.EditForm] || false,
        [UserFormPermissions.ManagePayments]: currentUserFormPermissionMap[UserFormPermissions.ManagePayments] || false,
        [UserFormPermissions.Receipt]: currentUserFormPermissionMap[UserFormPermissions.Receipt] || false,
      };

  const defaultGeneralPermissionsData = defaultGeneralPermissions
    ? defaultGeneralPermissions?.reduce(
        (acc, permission) => {
          acc[permission] = currentUserGeneralPermissionMap[permission] || false;
          return acc;
        },
        {} as Record<UserGeneralPermissions, boolean>
      )
    : {
        [UserGeneralPermissions.Contacts]: currentUserGeneralPermissionMap[UserGeneralPermissions.Contacts] || false,
        [UserGeneralPermissions.UserManagement]:
          currentUserGeneralPermissionMap[UserGeneralPermissions.UserManagement] || false,
        [UserGeneralPermissions.Bank]: currentUserGeneralPermissionMap[UserGeneralPermissions.Bank] || false,
        [UserGeneralPermissions.TapToPay]: currentUserGeneralPermissionMap[UserGeneralPermissions.TapToPay] || false,
      };

  const isAllCampaignsDisabled = Boolean(currentUser?.newPermissions?.permissionFormIds?.length);

  // State management layer
  const {
    form,
    selectedCampaigns,
    setSelectedCampaigns,
    campaignListOptions,
    formPermissionsControllers,
    generalPermissionsControllers,
    notificationsControllers,
    isMFAStep,
    code,
    setCode,
    onSubmit,
    maskedPhoneNumber,
    mfaHelpers,
    campaignScopeOptions,
    selectedCampaignScope,
    setSelectedCampaignScope,
  } = useUserPermissionState({
    isAllCampaignsDisabled,
    defaultUserData: {
      email: defaultEmail,
    },
    defaultFormPermissions: defaultFormPermissionsData,
    defaultDesabledFormPermissions: {
      [UserFormPermissions.EditForm]: !currentUserFormPermissionMap[UserFormPermissions.EditForm],
      [UserFormPermissions.ManagePayments]: !currentUserFormPermissionMap[UserFormPermissions.ManagePayments],
      [UserFormPermissions.Receipt]: !currentUserFormPermissionMap[UserFormPermissions.Receipt],
    },
    defaultDesabledGeneralPermissions: {
      [UserGeneralPermissions.Contacts]: !currentUserGeneralPermissionMap[UserGeneralPermissions.Contacts],
      [UserGeneralPermissions.UserManagement]: !currentUserGeneralPermissionMap[UserGeneralPermissions.UserManagement],
      [UserGeneralPermissions.Bank]: !currentUserGeneralPermissionMap[UserGeneralPermissions.Bank],
      [UserGeneralPermissions.TapToPay]: !currentUserGeneralPermissionMap[UserGeneralPermissions.TapToPay],
    },
    defaultGeneralPermissions: defaultGeneralPermissionsData,
    defaultNotifications: true,
    defaultDesabledNotifications: false,
    onSubmitCallback: async ({ data, selectedCampaigns, code }) => {
      setErrorMessage(undefined);
      if (
        (selectedCampaigns.length === 0 && selectedCampaignScope?.value !== "all") ||
        (isAllCampaignsDisabled && selectedCampaigns.length === 0)
      ) {
        setErrorMessage(t("dashboard", "userPermissionModal.settings.errorMessage.selectCampaign"));
        return;
      }

      if (source === "share-modal") {
        logAmplitudeEvent(AmplitudeEvents.InviteUserDuringActivationInvited, { user_type: "Team Member" });
      } else if (source === "bank-stepper") {
        logAmplitudeEvent(AmplitudeEvents.InviteUserDuringActivationInvited, { user_type: "Accountant" });
      } else if (source === "form-editor") {
        logAmplitudeEvent(AmplitudeEvents.NewUserInvited, { user_type: "Form collaborator" });
      } else {
        logAmplitudeEvent(AmplitudeEvents.NewUserInvited);
      }

      const formPermissionsInput = (Object.keys(data.formPermissions) as UserFormPermissions[]).filter(
        (key) => data.formPermissions[key]
      );

      const generalPermissionsInput = (Object.keys(data.generalPermissions) as UserGeneralPermissions[]).filter(
        (key) => data.generalPermissions[key]
      );

      const permissionsInput = mapNewPermissionsToOldPermissions({
        generalPermissions: generalPermissionsInput,
        formPermissions: formPermissionsInput,
      });

      const permissionFormIdsInput = selectedCampaigns.map((campaign) => campaign.value);

      const { error, data: res } = await execAddMemberToOrganization({
        email: data.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        locale,
        permissions: permissionsInput,
        formPermissions: formPermissionsInput,
        generalPermissions: generalPermissionsInput,
        permissionFormIds: formId ? [formId] : permissionFormIdsInput.length > 0 ? permissionFormIdsInput : undefined,
        ...(code ? { code } : {}),
        ...(formId && formType
          ? {
              notificationSettings: {
                isEnabled: data.notifications,
                formId,
                formType,
              },
            }
          : {}),
        source: source || "default",
        formLink,
      });

      if (error) {
        enqueueSnackbar(
          t(
            "dashboard",
            error.code === "userAlreadyExists"
              ? "settings.organization.userManagement.userAlreadyExists"
              : error.code === "userDoesNotHavePermission"
                ? "settings.organization.userManagement.userAlreadyExists"
                : error.code === "authenticate_sms_otp_code_incorrect"
                  ? "signin.twoFactor.wrongCode"
                  : "settings.organization.userManagement.otherError",
            {
              email: data.email,
            }
          ),
          { vibe: "danger" }
        );
        return;
      }

      if (res?.requiresSmsCode) {
        logAmplitudeEvent(AmplitudeEvents.TwoFAPrompted, {
          moment: "user with bank rights invited",
        });
        return { requiresSmsCode: true };
      }

      if (code) {
        logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, {
          moment: "user with bank rights invited",
        });
      }

      enqueueSnackbar(t("dashboard", `settings.organization.userManagement.creationSuccess`), { vibe: "positive" });
      if (refetchTable) {
        await refetchTable();
      }
      onClose();
    },
  });

  useEffect(() => {
    if (selectedCampaigns.length > 0 || selectedCampaignScope?.value === "all") {
      setErrorMessage(undefined);
    }
  }, [selectedCampaigns, selectedCampaignScope?.value]);

  const onFormEdition = !!formId;

  // Layout + Components layer
  return (
    <UserPermissionModalLayout
      icon={
        <DashboardIcon vibe="text-brand-moderate">
          <UserAddIcon />
        </DashboardIcon>
      }
      isOpen={isOpen}
      onClose={onClose}
      isMFAStep={isMFAStep}
      actions={{
        main: {
          onClick: onSubmit,
          isLoading: isLoadingAddMemberToOrganization,
          disabled: isMFAStep && mfaHelpers.isDisabledSubmitCodeButton,
          children: t("dashboard", "userPermissionModal.settings.sendInvitation"),
          ["data-test"]: "submit-button",
        },
        cancel: {
          onClick: onClose,
          children: t("dashboard", "cancel"),
          ["data-test"]: "cancel",
        },
      }}
      slots={{
        header: (
          <DashboardTypography variant="h6">
            {title || t("dashboard", "userPermissionModal.settings.title")}
          </DashboardTypography>
        ),
        userInputs: <UserInputsForm form={form} isOnlyEmail={!!formId} />,
        campaignPermissions: (
          <CampaignPermissionsSection
            slots={{
              title: onFormEdition
                ? t("dashboard", "userPermissionModal.form.campaignSpecificPermissions")
                : t("dashboard", "userPermissionModal.campaignSpecificPermissions"),
              campaignSelector: onFormEdition ? (
                <></>
              ) : (
                <CampaignScopeAutocomplete
                  campaignOptions={campaignListOptions}
                  selectedCampaigns={selectedCampaigns}
                  setSelectedCampaigns={setSelectedCampaigns}
                  campaignScopeOptions={campaignScopeOptions}
                  selectedCampaignScope={selectedCampaignScope}
                  setSelectedCampaignScope={setSelectedCampaignScope}
                  isAllCampaignsDisabled={isAllCampaignsDisabled}
                />
              ),
            }}
            permissions={{
              editForm: {
                checked: formPermissionsControllers.editForm.field.value,
                onChange: (checked) => formPermissionsControllers.editForm.field.onChange(checked),
              },
              managePayments: {
                checked: formPermissionsControllers.managePayments.field.value,
                onChange: (checked) => formPermissionsControllers.managePayments.field.onChange(checked),
              },
              receipt: {
                checked: formPermissionsControllers.receipt.field.value,
                onChange: (checked) => formPermissionsControllers.receipt.field.onChange(checked),
              },
            }}
          />
        ),
        generalPermissions: onFormEdition ? (
          <></>
        ) : (
          <GeneralPermissionsList
            permissions={{
              contacts: {
                checked: generalPermissionsControllers.contacts.field.value,
                onChange: (checked) => generalPermissionsControllers.contacts.field.onChange(checked),
              },
              bank: {
                checked: generalPermissionsControllers.bank.field.value,
                onChange: (checked) => generalPermissionsControllers.bank.field.onChange(checked),
              },
              userManagement: {
                checked: generalPermissionsControllers.userManagement.field.value,
                onChange: (checked) => generalPermissionsControllers.userManagement.field.onChange(checked),
              },
              tapToPay: {
                checked: generalPermissionsControllers.tapToPay.field.value,
                onChange: (checked) => generalPermissionsControllers.tapToPay.field.onChange(checked),
              },
            }}
          />
        ),
        notifications:
          formId || onFormEdition ? (
            <NotificationsSection
              permissions={{
                notification: {
                  checked: notificationsControllers.notification.field.value,
                  onChange: (checked) => notificationsControllers.notification.field.onChange(checked),
                },
              }}
              disabled={notificationsControllers.notification.field.disabled}
            />
          ) : (
            <></>
          ),
        errorMessage: errorMessage ? (
          <DashboardInfobox size="small" type="alert" vibe="danger">
            {errorMessage}
          </DashboardInfobox>
        ) : undefined,
        mfaStep: isMFAStep ? (
          <UserPermissionMFAStep
            code={code}
            setCode={setCode}
            maskedPhoneNumber={maskedPhoneNumber || undefined}
            onResendCode={mfaHelpers.resendCode}
            isResendDisabled={mfaHelpers.isResendButtonDisabled}
            resendCountdownText={mfaHelpers.resendButtonCountdownText}
            isLoadingResend={mfaHelpers.isLoadingResendSms}
            isDisabledSubmitCodeButton={mfaHelpers.isDisabledSubmitCodeButton}
            onSubmitCode={onSubmit}
            isLoadingSubmitCode={isLoadingAddMemberToOrganization}
          />
        ) : undefined,
      }}
    />
  );
};

export default InviteUserPermissionModal;
